import { useMemo, useState } from 'react'
import {
  CloseButton,
  Badge,
  Box,
  Button,
  HStack,
  Input,
  NativeSelect,
  SimpleGrid,
  Table,
  Text,
  Textarea,
  Field,
  Dialog,
} from '@chakra-ui/react';
import { FiAlertTriangle } from 'react-icons/fi'
import { toaster } from '@/components/ui/toaster'
import { useImportLegislators } from '../../hooks/useLobbyDay'
import { useConstituentCounts } from '../../hooks/useGeoDistricts'
import { districtUnitTypeFor } from './districtCatalog'
import { ResponsiveModal } from '../shared'
import type { Chamber, SaveLegislatorRequest } from '../../types/lobbyDay'

const MAX_PROBES = 200

const HEADER_WORDS = new Set(['FIRSTNAME', 'FIRST', 'SENATOR', 'REP', 'REPRESENTATIVE', 'NAME'])

/** Excel numeric cells paste as "319.0" — restore "319". */
const cleanOffice = (v: string) => v.replace(/\.0$/, '')

/** Parses spreadsheet-pasted TSV in Andrew's column order (header row optional):
 *  First  Last  District  Party  Office  Phone  Email
 *  Chamber/state are constant for the whole paste (picked in the modal). */
export function parsePastedLegislators(
  text: string,
  chamber: Chamber,
  state: string,
): SaveLegislatorRequest[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.split('\t').map((c) => c.trim()))
    .filter((cells) => cells.length >= 3 && cells[0] && cells[1])
    .filter((cells) => !HEADER_WORDS.has(cells[0].toUpperCase()))
    .map((cells) => ({
      firstName: cells[0],
      lastName: cells[1],
      chamber,
      district: cells[2] ?? '',
      state: state.toUpperCase(),
      party: cells[3] || null,
      officeRoom: cells[4] ? cleanOffice(cells[4]) : null,
      phone: cells[5] || null,
      email: cells[6] || null,
    }));
}

interface LegislatorImportModalProps {
  isOpen: boolean
  onClose: () => void
}

export function LegislatorImportModal({ isOpen, onClose }: LegislatorImportModalProps) {
  const importMutation = useImportLegislators()
  const [text, setText] = useState('')
  const [chamber, setChamber] = useState<Chamber>('StateSenate')
  const [state, setState] = useState('')
  const rows = useMemo(
    () => (state.trim().length === 2 ? parsePastedLegislators(text, chamber, state) : []),
    [text, chamber, state],
  )

  // Probe EVERY parsed row's district, not just the ten the preview renders — a bad district on
  // row 47 must not be invisible. Deduped, because a chamber+state paste repeats few districts.
  // Each row carries its own `state` (parsePastedLegislators stamps the modal's State field onto
  // every row), so the dedupe key AND the zero-match lookup below are `state|district`, not
  // district alone — otherwise two rows with the same district string in different states would
  // collapse into one probe and a mismatch on one state's district could flag the other state's
  // row. (Today this modal only ever produces one state per paste, since State is a single field
  // above the textarea, but the row shape carries a per-row state and the code should not assume
  // that invariant silently.)
  const rowKey = (r: { district: string; state: string }) => `${r.state}|${r.district.trim()}`

  const probes = useMemo(() => {
    if (districtUnitTypeFor(chamber) === null) return []
    const seen = new Set<string>()
    const out: { chamber: Chamber; district: string; state: string }[] = []
    for (const r of rows) {
      const d = r.district.trim()
      if (!d) continue
      const key = rowKey(r)
      if (seen.has(key)) continue
      seen.add(key)
      if (out.length >= MAX_PROBES) break
      out.push({ chamber, district: d, state: r.state })
    }
    return out
  }, [rows, chamber])

  // Deliberately a SECOND, independent scan over `rows`, not a reuse of `probes`'s `seen` set:
  // that set stops growing the instant the loop above `break`s at MAX_PROBES, so past the cap it
  // no longer reflects every distinct district. Collapsing these two loops into one would
  // reintroduce exactly the seen/break coupling `uncheckedDistricts` exists to stay honest about.
  const distinctDistrictCount = useMemo(() => {
    const seen = new Set<string>()
    for (const r of rows) {
      if (r.district.trim()) seen.add(rowKey(r))
    }
    return seen.size
  }, [rows])
  const uncheckedDistricts = Math.max(0, distinctDistrictCount - probes.length)

  const { data: counts, error: countsError } = useConstituentCounts(probes, probes.length > 0)

  const zeroDistricts = useMemo(
    () => new Set((counts ?? []).filter((c) => c.count === 0).map((c) => `${c.state}|${c.district}`)),
    [counts],
  )
  const flaggedRowCount = useMemo(
    () => rows.filter((r) => zeroDistricts.has(rowKey(r))).length,
    [rows, zeroDistricts],
  )

  const doImport = async () => {
    try {
      const result = await importMutation.mutateAsync(rows)
      toaster.create({ type: 'success', title: `Imported ${result.added} legislators`, description: result.skipped ? `${result.skipped} skipped (duplicates or invalid)` : undefined })
      setText('')
      onClose()
    } catch {
      toaster.create({ type: 'error', title: 'Import failed' })
    }
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="4xl">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>Paste legislators from a spreadsheet</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} mb={3}>
              <Field.Root w="full">
                <Field.Label fontSize="sm">Chamber (applies to all rows)</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    minH="44px"
                    value={chamber}
                    onChange={(e) => setChamber(e.target.value as Chamber)}>
                    <option value="StateSenate">State Senate</option>
                    <option value="StateHouse">State House</option>
                    <option value="USHouse">US House</option>
                    <option value="USSenate">US Senate</option>
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              <Field.Root w="full">
                <Field.Label fontSize="sm">State</Field.Label>
                <Input size="sm" minH="44px" value={state} onChange={(e) => setState(e.target.value)}
                  placeholder="MA" maxLength={2} />
              </Field.Root>
            </SimpleGrid>
            <Text fontSize="sm" color="gray.600" mb={2}>
              Copy rows from Excel/Sheets and paste below. Columns in order:
              First name, Last name, District, Party, Office, Phone, Email.
            </Text>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={8}
              aria-label="Paste legislator rows"
              placeholder={'Michael\tBarrett\tThird Middlesex\tDemocrat\t312-B\t(617) 722-1572\tMike.Barrett@masenate.gov'}
              fontFamily="mono" fontSize="xs" />
            {text.trim() && state.trim().length !== 2 && (
              <Text fontSize="sm" color="orange.500" mt={2}>Enter the two-letter state code to preview rows.</Text>
            )}
            {flaggedRowCount > 0 && (
              <HStack
                mt={3} gap={2} bg="orange.50" borderWidth="1px" borderColor="orange.300"
                borderRadius="md" p={3} align="flex-start">
                <Box color="orange.600" pt="2px"><FiAlertTriangle /></Box>
                <Text fontSize="sm" color="orange.900">
                  {flaggedRowCount} of {rows.length} row{rows.length === 1 ? '' : 's'}{' '}
                  {flaggedRowCount === 1 ? 'has' : 'have'} a district matching no constituents in
                  your contacts. They will import, but gap-fill and coverage will show them empty —
                  check the district spelling first.
                </Text>
              </HStack>
            )}
            {uncheckedDistricts > 0 && (
              <Text fontSize="xs" color="gray.600" mt={2}>
                District coverage was checked for the first {MAX_PROBES} distinct districts;{' '}
                {uncheckedDistricts} more were not checked.
              </Text>
            )}
            {!!countsError && (
              <Text fontSize="xs" color="orange.700" mt={2}>
                District coverage check unavailable — rows will still import.
              </Text>
            )}
            {rows.length > 0 && (
              <Box overflowX="auto" mt={4}>
                <Table.Root size="sm">
                  <Table.Header bg="gray.50">
                    <Table.Row><Table.ColumnHeader>Name</Table.ColumnHeader><Table.ColumnHeader>Chamber</Table.ColumnHeader><Table.ColumnHeader>District</Table.ColumnHeader><Table.ColumnHeader>Party</Table.ColumnHeader><Table.ColumnHeader>Office</Table.ColumnHeader></Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {rows.slice(0, 10).map((r, i) => (
                      <Table.Row key={i}>
                        <Table.Cell>{r.firstName} {r.lastName}</Table.Cell>
                        <Table.Cell><Badge>{r.chamber}</Badge></Table.Cell>
                        <Table.Cell>
                          <HStack gap={1}>
                            <Text as="span">{r.district}</Text>
                            {zeroDistricts.has(rowKey(r)) && (
                              <Box
                                as="span" color="orange.600"
                                title="No constituents match this district"
                                aria-label="No constituents match this district">
                                <FiAlertTriangle />
                              </Box>
                            )}
                          </HStack>
                        </Table.Cell>
                        <Table.Cell>{r.party ?? '—'}</Table.Cell><Table.Cell>{r.officeRoom ?? '—'}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
            {rows.length > 10 && <Text fontSize="xs" color="gray.500" mt={1}>…and {rows.length - 10} more</Text>}
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorPalette="brand" disabled={rows.length === 0}
              loading={importMutation.isPending} onClick={doImport}>
              Import {rows.length} rows
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
